import { useTranslation } from "react-i18next";
import { IconBackground } from "@/components/ui/icon-background";
import { FloatingAccents } from "@/components/decorative";
import { Gauge, BookOpen, TrendingUp, ChevronRight } from "lucide-react";

const steps = [
  {
    number: "1",
    icon: Gauge,
    titleKey: "howItWorks.step1.title",
    descriptionKey: "howItWorks.step1.description",
    gradient: "orange" as const,
  },
  {
    number: "2",
    icon: BookOpen,
    titleKey: "howItWorks.step2.title",
    descriptionKey: "howItWorks.step2.description",
    gradient: "teal" as const,
  },
  {
    number: "3",
    icon: TrendingUp,
    titleKey: "howItWorks.step3.title",
    descriptionKey: "howItWorks.step3.description",
    gradient: "purple" as const,
  },
];

interface HowItWorksProps {
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
}

export const HowItWorks = ({ isExpanded, setIsExpanded }: HowItWorksProps) => {
  const { t } = useTranslation();

  return (
    <section id="how-it-works" className="relative py-16 md:py-24 px-4 overflow-hidden">
        <FloatingAccents position="both" />
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <h2 className="font-clash text-3xl xs:text-4xl md:text-5xl font-bold text-center mb-12 text-foreground tracking-tight">
            {t("howItWorks.headline")}
          </h2>

          <div className="grid md:grid-cols-3 gap-12">
            {steps.map((step) => (
              <div key={step.number} className="text-center animate-fade-in">
                {/* Icon Background */}
                <IconBackground 
                  icon={step.icon}
                  gradient={step.gradient}
                  size="md"
                  className="mx-auto mb-6"
                />

                {/* Title */}
                <h3 className="font-clash text-xl font-semibold mb-3 text-foreground tracking-tight">
                  {step.number}. {t(step.titleKey)}
                </h3>

                {/* Description */}
                <p className="text-base text-foreground/80 leading-relaxed tracking-wide font-medium">
                  {t(step.descriptionKey).split('\n').map((line, index, array) => (
                    <span 
                      key={index}
                      className={index === array.length - 1 && step.number === "1" ? 'opacity-50 italic' : ''}
                    >
                      {line}
                      {index < array.length - 1 && <br />}
                    </span>
                  ))}
                </p>
              </div>
            ))}
          </div>

          {/* Trigger for Sustainability Pro Section */}
          <div className="mt-24 text-center">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="group inline-flex items-center gap-2 text-base text-muted-foreground hover:text-foreground transition-colors duration-300"
              aria-expanded={isExpanded}
              aria-controls="sustainability-pro-section"
            >
              <span>{t("howItWorks.sustainabilityProTrigger")}</span>
              <ChevronRight 
                className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
                style={{
                  background: "linear-gradient(90deg, hsl(99, 98%, 40%), hsl(330, 100%, 60%), hsl(263, 52%, 52%))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              />
            </button>
          </div>
        </div>
      </section>
  );
};
